import { Box, Paper, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { useMaterialUIController } from "../../context";
import { useClientBranding } from "../../providers/ClientBrandingProvider";

export type AuthPageShellProps = {
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  /** Optional brand / logo above the title. Defaults to Connect AI mark. */
  brand?: ReactNode;
  footer?: ReactNode;
  maxWidth?: number | string;
  /** Hide the default brand mark when true. */
  hideBrand?: boolean;
};

/**
 * Centered Fluent card shell for login, register, invite, setup, and public share.
 */
export default function AuthPageShell({
  title,
  subtitle,
  children,
  brand,
  footer,
  maxWidth = 440,
  hideBrand = false,
}: AuthPageShellProps) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { brandName, brandImageLight, brandImageDark } = useClientBranding();

  const defaultBrand = (
    <Box
      component="img"
      src={darkMode ? brandImageLight : brandImageDark}
      alt={brandName}
      sx={{
        display: "block",
        height: { xs: 64, sm: 76 },
        maxWidth: "100%",
        width: "auto",
        objectFit: "contain",
        alignSelf: "flex-start",
      }}
    />
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: { xs: 2.5, sm: 3 },
        background: (theme) =>
          theme.palette.mode === "dark"
            ? "radial-gradient(1000px 520px at 15% -10%, rgba(131,186,183,0.10), transparent 60%), #08090C"
            : "radial-gradient(1000px 520px at 15% -10%, rgba(131,186,183,0.22), transparent 60%), radial-gradient(800px 480px at 95% 5%, rgba(141,160,96,0.12), transparent 55%), #F4F8F7",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: 1,
          maxWidth,
          p: { xs: 2.5, sm: 3 },
          borderRadius: 1,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          boxShadow: (theme) => (theme as any).boxShadows.lg,
        }}
      >
        <Stack spacing={2}>
          {hideBrand ? null : brand ?? defaultBrand}
          <Box>
            <Typography variant="h4" component="h1" fontWeight={700} gutterBottom sx={{ mb: 0.5 }}>
              {title}
            </Typography>
            {subtitle != null &&
              (typeof subtitle === "string" || typeof subtitle === "number" ? (
                <Typography variant="body2" color="text.secondary">
                  {subtitle}
                </Typography>
              ) : (
                subtitle
              ))}
          </Box>
          {children}
          {footer}
        </Stack>
      </Paper>
    </Box>
  );
}
